import React from "react";

import moment from "moment";

import { toast } from "react-toastify";

import * as AnalyticsAPI from "api/AnalyticsAPI";

import * as globalReducer from "containers/App/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import { isEnabled } from "utils/featureFlags";
import * as loginReducer from "containers/Login/reducer";

const UPDATE_FIELD = "analytics/UPDATE_FIELD";

const initialState = {};

function analyticsReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    default:
      return state;
  }
}

export function fetchAnalytics(skipCache = false) {
  return (dispatch, getState) => {
    if (!isEnabled("user-activity-analytics")) {
      return Promise.resolve();
    }
    dispatch(globalReducer.updateIsAnalyticsLoading(true));
    const accounts = accountsReducer.getActiveAccounts(getState());
    return Promise.all(
      accounts.map((account) => {
        return dispatch(fetchAccountAnalytics(account?.id, skipCache));
      })
    )
      .then(() => {
        dispatch(globalReducer.updateIsAnalyticsLoading(false));
      })
      .catch(() => {
        dispatch(globalReducer.updateIsAnalyticsLoading(false));
      });
  };
}

export function fetchAccountAnalytics(accountId, skipCache = false) {
  return (dispatch, getState) => {
    return AnalyticsAPI.fetchAnalytics(accountId, skipCache)
      .then((res) => {
        dispatch(updateAnalytics(accountId, res));
        return res;
      })
      .catch((e) => {
        console.log(e);
        return Promise.resolve();
      });
  };
}

/*--Selectors--*/

export function getBase(state) {
  return state.analytics;
}

export function getAnalytics(state, id) {
  return getBase(state)[id];
}

export function getAnalyticsList(state, tenantIdOverride) {
  const tenantId = tenantIdOverride ?? loginReducer.getSelectedTenant(state)?.id;
  const analytics = getBase(state);
  const accountIds = Object.keys(analytics).filter((aId) => {
    const account = accountsReducer.getAccount(state, aId);
    if (!tenantId) {
      return true;
    }
    return account?.tenant_id === tenantId;
  });

  return accountIds.map((aId) => {
    return getAnalytics(state, aId);
  });
}

export function getHasAnalytics(state) {
  return getAnalyticsList(state)?.length > 0;
}

export function getTotalValue(state, valueKey) {
  const allAnalytics = getAnalyticsList(state) ?? [];
  const total = allAnalytics.reduce((acc, analytic) => {
    const currVal = analytic?.data?.[valueKey] ?? 0;
    return acc + currVal;
  }, 0);

  return total;
}

export function getChatCount(state) {
  return getTotalValue(state, "chat_count");
}

export function getDocumentSetCount(state) {
  return getTotalValue(state, "document_set_count");
}
export function getDocumentCount(state) {
  return getTotalValue(state, "document_count");
}
export function getTotalDocumentSize(state) {
  return getTotalValue(state, "total_document_size");
}
export function getDatasetCount(state) {
  return getTotalValue(state, "dataset_count");
}
export function getDatasetRowCount(state) {
  return getTotalValue(state, "dataset_row_count");
}
export function getDatasetCombinationCount(state) {
  return getTotalValue(state, "combination_count");
}
export function getModelCount(state) {
  return getTotalValue(state, "model_count");
}

function parseDate(analyticsId) {
  const values = String(analyticsId).split("-");
  const date = values.slice(0, 3);
  const time = values.slice(3, 6);
  const dateTime = date.join("-") + " " + time.join(":");

  const dMoment = moment.utc(dateTime);

  return dMoment.format();
}

export function getAnalyticsRefreshDate(state) {
  const allAnalytics = getAnalyticsList(state) ?? [];
  const earliestDate = allAnalytics.reduce((acc, analytic) => {
    const currDate = analytic?.upper_cutoff ?? parseDate(analytic?.id);
    if (!currDate || analytic?.status !== "COMPLETE") {
      return acc;
    }
    if (acc === undefined) {
      return currDate;
    }
    if (moment.utc(currDate).isBefore(acc)) {
      return currDate;
    }
    return acc;
  }, undefined);

  return earliestDate;
}

export function getAnalyticsData(state) {
  const chat_count = getChatCount(state);

  const document_set_count = getDocumentSetCount(state);
  const document_count = getDocumentCount(state);
  const total_document_size = getTotalDocumentSize(state);

  const dataset_count = getDatasetCount(state);
  const dataset_row_count = getDatasetRowCount(state);
  const combination_count = getDatasetCombinationCount(state);

  const model_count = getModelCount(state);

  return {
    chat_count,
    document_set_count,
    document_count,
    total_document_size,
    dataset_count,
    dataset_row_count,
    combination_count,
    model_count
  };
}
/*--Action Creators--*/

export function updateAnalytics(id, data) {
  return { type: UPDATE_FIELD, data: data, key: id };
}

export default analyticsReducer;
